// =================================================================================================
// >>> **THE LIVE EVENT, ON TWO SCREENS: HERS AND THEIRS.** <<<
//
// EV3's one tap and countdown, and EV6's cost ticker. Both surfaces of one night, in one file,
// because they read the same two routes and a split would be two answers to *is the house open*.
//
// **THIS IS A NEW FILE RATHER THAN MORE OF `src/App.tsx`** — Item 7's standing order is to move
// surfaces out opportunistically as areas are touched, and a surface she taps under pressure on a
// live night is exactly the kind that must be findable.
//
// ---------------------------------------------------------------------------------------------
// >>> **NEITHER SURFACE KEEPS A TICKING COUNTER.** <<<
//
// Every second is `endsAt - now` through `eventCountdown`, the same law the server's window is
// stored to support. A decremented number drifts, and a phone that slept through the first half of
// her event wakes up confidently wrong — which on this one screen is the difference between a member
// who thinks she has twenty minutes and a door that shut ten minutes ago.
// =================================================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { eventCountdown } from "../lib/entitlements";
import { loadOwnerData, postOwnerAction, ownerActionNote } from "../lib/owner-load";

type WindowAnswer = {
  ok?: boolean;
  open?: boolean;
  secondsLeft?: number;
  endsAt?: number | null;
  state?: string;
};

/** WINDOW 31 ITEM 1 — what `/api/live/mail-pause/status` answers. Owner-only; never member-facing. */
type MailPauseAnswer = {
  ok?: boolean;
  paused?: boolean;
  state?: string;
  secondsLeft?: number;
  endsAt?: number | null;
  message?: string;
  /** The server own default pause length. The one declaration lives in server/live-mail-pause.js. */
  defaultMinutes?: number;
};

/**
 * >>> **THERE IS NO SECOND COPY OF THE PAUSE LENGTH ON THIS SIDE (R28/R29).** <<<
 *
 * The first cut of this component declared its own `180` beside a comment claiming it was the
 * server's number, which is the two-answer-keys disease with a sentence on top. **R203 caught it**:
 * the server was already sending `defaultMinutes` and nothing read it. The status read carries the
 * number now, and an omitted one lets the server apply its own default — so the length is declared
 * in exactly one place, `server/live-mail-pause.js`.
 */

type Ticker = {
  state?: string;
  say?: string;
  spentCents?: number | null;
  perHourCents?: number | null;
  crossed?: boolean;
};

type SpendAnswer = {
  ticker?: Ticker;
  ledgerRead?: boolean;
  calls?: number | null;
  window?: {
    open?: boolean;
    state?: string;
    endsAt?: number | null;
    secondsLeft?: number;
    countingFrom?: string;
    /** ITEM 6e: what the number IS, from server/spend-window.js ("AI cost today" / "This event so far"). */
    label?: string;
  };
  alarmed?: boolean;
};

/**
 * How often the member banner asks the server whether the house is still open.
 *
 * **THE SERVER MEMOISES THAT ROW FOR FIVE SECONDS**, so a thousand phones on this interval are at
 * most twelve real queries a minute. The seconds between polls are counted locally off `endsAt`,
 * which is why a slow poll costs the countdown no accuracy at all.
 */
const WINDOW_POLL_MS = 20_000;

/** And how often her own screen re-reads the bill. Owner-only, one reader, so it can be brisk. */
const SPEND_POLL_MS = 30_000;

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

/**
 * >>> **THE WINDOW, POLLED.** <<< Returns what the server last said plus a `refreshedAt` that moves
 * on every answer, so a caller can re-read whatever else the drop changed.
 *
 * **A FAILED POLL DOES NOT CLOSE THE BANNER.** The countdown is computed from `endsAt`, so a member
 * whose phone loses signal mid-event keeps a correct countdown and it runs out on its own. Blanking
 * the banner on one dropped fetch would flicker the whole thing off and on across a whole room.
 */
function useEventWindow(enabled: boolean) {
  const [win, setWin] = useState<WindowAnswer | null>(null);
  const [beat, setBeat] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const read = () => {
        // >>> **READ THE BODY ON EVERY PATH, INCLUDING THE ONES THAT THROW IT AWAY (2026-09-18).** <<<
        // `r.ok ? r.json() : null` LEAVES A NON-OK BODY UNCONSUMED. The browser then never marks the
        // request finished, and a page waiting for the network to go quiet waits for ever — which is
        // how one 403 on this route cost fifteen render units a 60-second navigation timeout each.
        // `.json()` on a body that is not JSON rejects, and the `.catch` below is what that is for.
      void fetch("/api/event/drop/status", { cache: "no-store" })
        .then((r) => r.json().then((d) => (r.ok ? d : null)))
        .then((d: WindowAnswer | null) => {
          if (!active || !d || d.ok !== true) return;
          setWin(d);
          setBeat((n) => n + 1);
        })
        .catch(() => {});
    };
    read();
    const id = window.setInterval(read, WINDOW_POLL_MS);
    return () => { active = false; window.clearInterval(id); };
  }, [enabled]);
  return { win, beat };
}

/**
 * >>> **WHAT A MEMBER SEES: THE HOUSE IS OPEN, AND FOR HOW MUCH LONGER.** <<<
 *
 * **AND IT RENDERS NOTHING ON AN ORDINARY DAY** — no window is not an expired window (R20). The
 * banner appears when she taps start and takes itself away when the window ends, with nobody
 * refreshing anything.
 */
export function EventCountdownBanner({
  signedIn,
  onWindowChange,
}: {
  signedIn: boolean;
  onWindowChange?: (open: boolean) => void;
}) {
  const { win } = useEventWindow(signedIn);
  const open = win?.open === true;
  const now = useNow(open);
  const countdown = eventCountdown({ eventOpen: open, eventEndsAt: win?.endsAt ?? 0 }, now);

  // **THE DOORS THIS CLIENT ALREADY DREW HAVE TO BE REDRAWN**, because the locks in a loaded page
  // came from an entitlements read taken before the window existed. The server is already honouring
  // the drop on every request; this is what makes the SCREEN agree with it.
  const wasOpen = useRef<boolean | null>(null);
  useEffect(() => {
    if (wasOpen.current === open) return;
    const first = wasOpen.current === null;
    wasOpen.current = open;
    if (!first) onWindowChange?.(open);
  }, [open, onWindowChange]);

  // >>> **THE BANNER MOVES THE PAGE DOWN RATHER THAN SITTING ON TOP OF IT.** <<<
  //
  // It has to be `fixed` — a member walks through rooms while the clock runs, so it follows her —
  // and the house's own `.nav` is `sticky; top: 0`, which means a fixed banner would cover the menu
  // for the whole event. **The height is MEASURED rather than guessed**, because this bar wraps
  // differently on a phone and carries an iPhone's safe-area inset on top of that, and a hard-coded
  // 34px would tuck the nav under it on exactly the devices most of her members are holding.
  const barRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!countdown.open) {
      document.body.classList.remove("has-event-banner");
      root.style.removeProperty("--event-banner-h");
      return;
    }
    document.body.classList.add("has-event-banner");
    const measure = () => {
      const h = barRef.current?.offsetHeight || 0;
      if (h) root.style.setProperty("--event-banner-h", `${h}px`);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      // **THE CLEANUP RUNS ON EVERY RAISE, INCLUDING THE AUTOMATIC ONE**, so the page does not keep
      // an inch of empty gold-lit space at the top after the window has closed itself.
      document.body.classList.remove("has-event-banner");
      root.style.removeProperty("--event-banner-h");
    };
  }, [countdown.open]);

  if (!countdown.open) return null;
  return (
    <div className="event-banner" role="status" aria-live="polite" ref={barRef}>
      <span className="event-banner__dot" aria-hidden="true" />
      <span className="event-banner__say">The house is open. Every room, every reading.</span>
      <span className="event-banner__clock">{countdown.label}</span>
    </div>
  );
}

function money(cents: number | null | undefined): string {
  if (typeof cents !== "number" || !Number.isFinite(cents)) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * >>> **WHAT SHE SEES: ONE TAP, ONE CLOCK, ONE NUMBER.** <<<
 *
 * It sits at the top of her dashboard rather than behind a tab, because on the night she is holding
 * a phone in one hand and talking to a camera. **Everything she needs is above the fold and nothing
 * she does here can refuse a member anything** — her ruling, 2026-09-09: the ticker notifies, it
 * never brakes.
 */
export function OwnerEventBar() {
  const { win, beat } = useEventWindow(true);
  const [spend, setSpend] = useState<SpendAnswer | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [minutes, setMinutes] = useState(60);
  // >>> **WINDOW 31 ITEM 1 — THE TESTING PAUSE, ON THE BAR SHE IS ALREADY HOLDING.** <<<
  //
  // Her order, 2026-09-15. It sits HERE rather than behind a settings tab for the same reason the
  // gate drop does: on a testing night she is on this screen with a phone in one hand, and a
  // safeguard she has to go looking for is one she will not flip.
  const [mailPause, setMailPause] = useState<MailPauseAnswer | null>(null);
  const mailPaused = mailPause?.paused === true;
  const open = win?.open === true;
  const now = useNow(open);
  const countdown = eventCountdown({ eventOpen: open, eventEndsAt: win?.endsAt ?? 0 }, now);

  const readSpend = useCallback(() => {
    void loadOwnerData<SpendAnswer>("/api/event/spend").then((answer) => {
      if (answer.ok) setSpend(answer.data);
      // A refused spend read leaves the LAST number standing rather than blanking to a zero — the
      // one lie this tile exists to stop telling.
    });
  }, []);

  useEffect(() => {
    readSpend();
    const id = window.setInterval(readSpend, SPEND_POLL_MS);
    return () => window.clearInterval(id);
  }, [readSpend, beat]);

  // The pause lifts itself, so her screen has to keep asking or it would go on showing PAUSED for
  // hours after it ended — the same reason the countdown above polls rather than latching.
  const readMailPause = useCallback(() => {
    void loadOwnerData<MailPauseAnswer>("/api/live/mail-pause/status").then((answer) => {
      if (answer.ok) setMailPause(answer.data);
    });
  }, []);

  useEffect(() => {
    readMailPause();
    const id = window.setInterval(readMailPause, SPEND_POLL_MS);
    return () => window.clearInterval(id);
  }, [readMailPause]);

  const toggleMailPause = async () => {
    setBusy(true);
    setNote("");
    const answer = await postOwnerAction<{ ok?: boolean; message?: string }>(
      `/api/live/mail-pause/${mailPaused ? "stop" : "start"}`,
      // The server default rides back on the status read; an absent one lets the server apply it.
      mailPaused || !mailPause?.defaultMinutes ? {} : { minutes: mailPause.defaultMinutes },
    );
    setBusy(false);
    setNote(answer.ok ? (answer.data?.message || "") : ownerActionNote(answer));
    readMailPause();
  };

  const act = async (what: "start" | "stop") => {
    setBusy(true);
    setNote("");
    const answer = await postOwnerAction<{ ok?: boolean }>(
      `/api/event/drop/${what}`,
      what === "start" ? { minutes } : {},
    );
    setBusy(false);
    setNote(answer.ok ? "" : ownerActionNote(answer));
    readSpend();
  };

  const ticker = spend?.ticker || null;
  const crossed = ticker?.crossed === true;
  const unread = ticker?.state === "unread";

  return (
    <section className={`event-bar${open ? " is-open" : ""}`}>
      <div className="event-bar__left">
        <h2 className="event-bar__title">{open ? "THE HOUSE IS OPEN" : "Gate drop"}</h2>
        {open ? (
          <p className="event-bar__clock">{countdown.label}</p>
        ) : (
          <p className="event-bar__hint">
            Every locked door opens for everyone, for the window you choose, and closes itself.
          </p>
        )}
      </div>

      <div className="event-bar__mid">
        {open ? (
          <button type="button" className="event-bar__btn is-stop" disabled={busy} onClick={() => void act("stop")}>
            Close it now
          </button>
        ) : (
          <>
            <label className="event-bar__mins">
              <span>Minutes</span>
              <input
                type="number"
                min={1}
                max={240}
                value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value) || 60)}
              />
            </label>
            <button type="button" className="event-bar__btn" disabled={busy} onClick={() => void act("start")}>
              Open the house
            </button>
          </>
        )}
      </div>

      <div className={`event-bar__spend${crossed ? " is-watch" : ""}${unread ? " is-unread" : ""}`}>
        <span className="event-bar__spend-label">
          {/* R18 — *not read* and *nothing spent* are different sentences and this says which. */}
          {unread ? `${spend?.window?.label || "AI cost"} — UNKNOWN` : (spend?.window?.label || "AI cost")}
        </span>
        <span className="event-bar__spend-figure">{unread ? "—" : money(ticker?.spentCents ?? null)}</span>
        <span className="event-bar__spend-rate">
          {ticker?.say || "Reading the ledger…"}
        </span>
        {spend?.window?.countingFrom ? (
          <span className="event-bar__spend-from">Counting from {spend.window.countingFrom}.</span>
        ) : null}
      </div>

      {/* >>> **WINDOW 31 ITEM 1 — THE TESTING PAUSE.** <<< Her order, 2026-09-15. On the 13th she
          went live ~27 times testing and each go-live emailed ~2,900 members with no phone alert.
          **The label says what is still working**, because a control that reads as "everything off"
          is one she second-guesses mid-broadcast. */}
      <div className={`event-bar__mailpause${mailPaused ? " is-paused" : ""}`}>
        <span className="event-bar__spend-label">{mailPaused ? "Test mode — emails paused" : "Live emails"}</span>
        <button type="button" className="event-bar__btn is-quiet" disabled={busy} onClick={() => void toggleMailPause()}>
          {mailPaused ? "Send emails again" : "Pause emails for testing"}
        </button>
        <span className="event-bar__spend-rate">
          {mailPause?.message
            || "Going live emails members who have no phone alert. Pause it while you test."}
        </span>
      </div>

      {note ? <p className="event-bar__note">{note}</p> : null}
    </section>
  );
}
